import { mergeSxProps, Text } from "@clipboard-health/ui-components";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";

import { ShieldIcon } from "./ShieldIcon";

const CREDIT_PROGRESS_BAR_COLORS = {
  blue: {
    gradient: "linear-gradient(90deg, rgb(75, 105, 165) 0%, rgb(110, 127, 174) 100%)",
    circleBackground: "linear-gradient(135deg, rgb(75, 105, 165) 0%, rgb(110, 127, 174) 100%)",
    background: "linear-gradient(90deg, rgb(89, 109, 163) 0%, rgb(110, 127, 174) 100%)",
  },
  green: {
    gradient: "linear-gradient(90deg, rgb(59, 106, 33) 0%, rgb(67, 144, 46) 100%)",
    circleBackground: "linear-gradient(135deg, rgb(59, 106, 33) 0%, rgb(67, 144, 46) 100%)",
    background: "linear-gradient(90deg, rgb(87, 165, 75) 0%, rgb(125, 163, 89) 100%)",
  },
} as const;

export type CreditProgressBarColor = keyof typeof CREDIT_PROGRESS_BAR_COLORS;

export interface CreditProgressBarProps {
  /**
   * Current progress count (e.g., 5 shifts completed)
   */
  currentCount: number;
  /**
   * Total count needed (e.g., 20 shifts total)
   */
  totalCount: number;
  /**
   * Optional label text displayed inside the progress bar
   * If not provided, no label will be shown
   */
  label?: string;
  /**
   * Color variant for the progress bar
   * @default "blue"
   */
  color?: CreditProgressBarColor;
  /**
   * Custom icon to display on the right side
   * @default ShieldIcon
   */
  icon?: ReactNode;
  /**
   * Custom sx prop for the container
   */
  sx?: SxProps<Theme>;
}

const PILL_HEIGHT = 24;
const CIRCLE_SIZE = 52;
const ICON_SIZE = 24;
// Border width creates the visual gap/concave effect
const BORDER_WIDTH = 3;

// Duration for the progress fill animation in milliseconds
const ANIMATION_DURATION_MS = 600;
// Delay before starting the animation (allows component to mount)
const ANIMATION_DELAY_MS = 100;
// Duration for the icon color transition
const ICON_TRANSITION_MS = 300;

export function CreditProgressBar(props: CreditProgressBarProps): ReactElement {
  const { currentCount, totalCount, label, color = "blue", icon, sx } = props;

  const progress = totalCount > 0 ? Math.max(0, Math.min(currentCount / totalCount, 1)) : 0;
  const isComplete = progress >= 1;
  const colorConfig = CREDIT_PROGRESS_BAR_COLORS[color];

  // Animated progress starts at 0 and animates to the actual progress value
  const [animatedProgress, setAnimatedProgress] = useState(0);
  // Track when the icon should show its "complete" state (for animation purposes)
  const [iconAnimated, setIconAnimated] = useState(false);

  useEffect(() => {
    const progressTimer = setTimeout(() => {
      setAnimatedProgress(progress);
    }, ANIMATION_DELAY_MS);

    // If progress is complete, animate the icon after the progress bar reaches it
    let iconTimer: ReturnType<typeof setTimeout> | undefined;
    if (isComplete) {
      // Icon transition starts when the progress bar animation completes
      const iconDelay = ANIMATION_DELAY_MS + ANIMATION_DURATION_MS;
      iconTimer = setTimeout(() => {
        setIconAnimated(true);
      }, iconDelay);
    } else {
      setIconAnimated(false);
    }

    return () => {
      clearTimeout(progressTimer);
      if (iconTimer) {
        clearTimeout(iconTimer);
      }
    };
  }, [progress, isComplete]);

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        },
        sx
      )}
    >
      {/* Progress bar container */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Pill container with concave right edge */}
        <Box
          sx={(theme) => ({
            display: "flex",
            flex: 1,
            height: PILL_HEIGHT,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            borderRadius: `${PILL_HEIGHT / 2}px`,
            overflow: "hidden",
            backgroundColor: theme.palette.background.tertiary,
            paddingLeft: 2,
            paddingRight: 4,
            zIndex: 0,
          })}
        >
          {/* Progress fill */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${animatedProgress * 100}%`,
              background: colorConfig.gradient,
              borderRadius: `${PILL_HEIGHT / 2}px`,
              transition: `width ${ANIMATION_DURATION_MS}ms ease-out`,
            }}
          />

          {/* Dark label text (visible over light background) */}
          {label && (
            <Text
              semibold
              color={(theme) => theme.palette.text.primary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}

          {/* White label text (clipped to progress width using clip-path) */}
          {label && (
            <Text
              semibold
              color={(theme) => theme.palette.text.invertPrimary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
                // Clip from the right to only show text over the progress fill
                clipPath: `inset(0 ${100 - animatedProgress * 100}% 0 0)`,
                transition: `clip-path ${ANIMATION_DURATION_MS}ms ease-out`,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}
        </Box>

        {/* Circular icon container */}
        <Box
          sx={(theme) => ({
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: theme.palette.background.tertiary,
            // Subtle shadow only when complete
            boxShadow: isComplete ? "0px 2px 4px rgba(0, 0, 0, 0.1)" : "none",
            // Border matches card background to create the concave illusion
            border: `${BORDER_WIDTH}px solid ${theme.palette.background.primary}`,
            // Overlap the pill so the border covers the edge, creating the concave look
            marginLeft: `-${CIRCLE_SIZE / 2}px`,
            zIndex: 1,
            position: "relative",
            overflow: "hidden",
          })}
        >
          {/* Colored background overlay that fills from left to right */}
          {isComplete && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: colorConfig.circleBackground,
                borderRadius: "50%",
                // Clip from right, revealing from left as animation progresses
                clipPath: iconAnimated ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
                transition: `clip-path ${ICON_TRANSITION_MS}ms ease-out`,
              }}
            />
          )}
          {icon ?? (
            <ShieldIcon
              variant={iconAnimated ? "filled" : "outlined"}
              sx={(theme) => ({
                width: ICON_SIZE,
                height: ICON_SIZE,
                color: iconAnimated ? "white" : theme.palette.text.tertiary,
                transition: `color ${ICON_TRANSITION_MS}ms ease-out`,
                position: "relative",
                zIndex: 1,
              })}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
